import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setStatus('error'); setMessage('Lien invalide.'); return; }

    api.get(`/auth/verify-email/${token}`)
      .then(({ data }) => {
        setStatus('success');
        // ✅ Auto-login : connecter l'utilisateur directement
        if (data.autoLogin && data.accessToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
          useAuthStore.setState({
            user: data.user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            isAuthenticated: true,
          });
          // Rediriger vers le bon dashboard après 2 secondes
          setTimeout(() => {
            navigate(data.user.role === 'CLIENT' ? '/dashboard' : '/espace-prestataire');
          }, 2500);
        }
      })
      .catch(err => {
        const data = err.response?.data;
        if (data?.expired) setStatus('expired');
        else { setStatus('error'); setMessage(data?.error || 'Lien de vérification invalide.'); }
      });
  }, [searchParams, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0D1B2A 0%, #1A3B5D 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px',
          padding: '3rem', maxWidth: '480px', width: '100%', textAlign: 'center',
        }}
      >
        {/* LOADING */}
        {status === 'loading' && (
          <>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              style={{ fontSize: '3rem', marginBottom: '1.5rem', display: 'inline-block' }}>⚙️</motion.div>
            <h2 style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: '0.5rem' }}>
              VÉRIFICATION EN COURS...
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Veuillez patienter.</p>
          </>
        )}

        {/* SUCCESS + AUTO-LOGIN */}
        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
              style={{ fontSize: '4rem', marginBottom: '1.5rem' }}
            >✅</motion.div>
            <h2 style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: '1rem', letterSpacing: '1px' }}>
              EMAIL VÉRIFIÉ !
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              Votre adresse email a été confirmée avec succès.
            </p>
            {/* Barre de progression avant redirection */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '100px', height: '4px', marginBottom: '1rem', overflow: 'hidden' }}>
              <motion.div
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 2.5, ease: 'linear' }}
                style={{ height: '100%', background: 'linear-gradient(90deg, #FF6B00, #CC4400)', borderRadius: '100px' }}
              />
            </div>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', marginBottom: '1.5rem' }}>
              Redirection vers votre espace en cours...
            </p>
            <Link to="/connexion" style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: 'white', padding: '0.75rem 1.5rem', borderRadius: '10px',
              textDecoration: 'none', fontSize: '0.85rem',
            }}>
              Aller à la connexion manuellement →
            </Link>
          </>
        )}

        {/* EXPIRÉ */}
        {status === 'expired' && (
          <>
            <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>⏰</div>
            <h2 style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: '1rem', letterSpacing: '1px' }}>
              LIEN EXPIRÉ
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, marginBottom: '2rem' }}>
              Ce lien a expiré (validité 24h). Entrez votre email pour en recevoir un nouveau.
            </p>
            {!resendDone ? (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setResendLoading(true);
                try {
                  await api.post('/auth/resend-verification', { email: resendEmail });
                  setResendDone(true);
                } catch (err) {
                  setMessage(err.response?.data?.error || 'Erreur lors de l\'envoi.');
                } finally { setResendLoading(false); }
              }} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <input
                  type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)}
                  placeholder="Votre email" required
                  style={{
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px', padding: '0.9rem 1.2rem', color: 'white',
                    fontSize: '0.95rem', outline: 'none',
                  }}
                />
                {message && <p style={{ color: '#EF4444', fontSize: '0.85rem' }}>{message}</p>}
                <button type="submit" disabled={resendLoading} style={{
                  background: 'linear-gradient(135deg, #FF6B00, #CC4400)',
                  border: 'none', color: 'white', padding: '0.9rem',
                  borderRadius: '10px', cursor: 'pointer', fontWeight: 700,
                  fontSize: '0.9rem', letterSpacing: '1px', opacity: resendLoading ? 0.7 : 1,
                }}>
                  {resendLoading ? 'Envoi...' : '📧 RENVOYER LE LIEN'}
                </button>
              </form>
            ) : (
              <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '12px', padding: '1rem' }}>
                <p style={{ color: '#10B981', fontSize: '0.95rem' }}>
                  ✅ Email envoyé ! Vérifiez votre boîte mail (et les spams).
                </p>
              </div>
            )}
          </>
        )}

        {/* ERREUR */}
        {status === 'error' && (
          <>
            <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>❌</div>
            <h2 style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: '1rem', letterSpacing: '1px' }}>
              LIEN INVALIDE
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              {message || 'Ce lien de vérification est invalide ou a déjà été utilisé.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {/* Formulaire pour renvoyer un nouveau lien */}
              {!resendDone ? (
                <>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
                    Entrez votre email pour recevoir un nouveau lien :
                  </p>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setResendLoading(true);
                    try {
                      await api.post('/auth/resend-verification', { email: resendEmail });
                      setResendDone(true);
                    } catch (err) {
                      setMessage(err.response?.data?.error || 'Erreur lors de l\'envoi.');
                    } finally { setResendLoading(false); }
                  }} style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <input
                      type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)}
                      placeholder="Votre email" required
                      style={{
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '10px', padding: '0.9rem 1.2rem', color: 'white',
                        fontSize: '0.95rem', outline: 'none',
                      }}
                    />
                    <button type="submit" disabled={resendLoading} style={{
                      background: 'linear-gradient(135deg, #FF6B00, #CC4400)',
                      border: 'none', color: 'white', padding: '0.85rem',
                      borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem',
                      opacity: resendLoading ? 0.7 : 1,
                    }}>
                      {resendLoading ? 'Envoi...' : '📧 RECEVOIR UN NOUVEAU LIEN'}
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '12px', padding: '1rem' }}>
                  <p style={{ color: '#10B981' }}>✅ Nouveau lien envoyé ! Vérifiez votre boîte mail.</p>
                </div>
              )}
              <Link to="/inscription" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.6)', padding: '0.75rem', borderRadius: '10px',
                textDecoration: 'none', fontSize: '0.85rem', marginTop: '0.4rem',
              }}>
                ← Retour à l'inscription
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
